import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { VideoControlSurface } from '../types/LiveKitVideoDisplay.types'

type LiveKitCallControlsContextValue = {
  surface: VideoControlSurface | null
  setSurface: (next: VideoControlSurface | null) => void
}

const LiveKitCallControlsContext =
  createContext<LiveKitCallControlsContextValue | null>(null)

export function LiveKitCallControlsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [surface, setSurfaceState] = useState<VideoControlSurface | null>(null)

  const setSurface = useCallback((next: VideoControlSurface | null) => {
    setSurfaceState(next)
  }, [])

  const value = useMemo(
    () => ({ surface, setSurface }),
    [surface, setSurface]
  )

  return (
    <LiveKitCallControlsContext.Provider value={value}>
      {children}
    </LiveKitCallControlsContext.Provider>
  )
}

/** Call/video toolbar surface when LiveKit UI is mounted; null otherwise. */
export function useLiveKitCallControls(): VideoControlSurface | null {
  const ctx = useContext(LiveKitCallControlsContext)
  if (!ctx) {
    throw new Error(
      'useLiveKitCallControls must be used within LiveKitCallControlsProvider'
    )
  }
  return ctx.surface
}

/** LiveKitVideoDisplayController registers merged state + actions here. */
export function useLiveKitCallControlsRegistration(): {
  setSurface: (next: VideoControlSurface | null) => void
} {
  const ctx = useContext(LiveKitCallControlsContext)
  if (!ctx) {
    throw new Error(
      'useLiveKitCallControlsRegistration must be used within LiveKitCallControlsProvider'
    )
  }
  return { setSurface: ctx.setSurface }
}
